






































import { Component, Inject, Vue } from "vue-property-decorator";
import { IImportProviderData, IImportProviderMethods } from "@/models/imports";
import { ImportStatusCodes as ISC } from "@/data/enums/imports";
import { TranslateResult } from "vue-i18n";
import _ from "@/boot/lodash";

interface IImportMessage {
  if: boolean;
  icon?: string;
  path: string;
  slots?: () => {
    component: string;
    name: string;
    props?: Record<string, any>;
    events?: Record<string, Function>;
    value?: string | number;
  }[];
  tag?: TranslateResult;
  type: string;
  action?: {
    label: TranslateResult;
    events?: Record<string, Function>;
    props?: Record<string, any>;
  };
}

@Component
export default class ImportMessages extends Vue {
  // Inject

  @Inject({ from: "importProvider" })
  public readonly $importProvider!: () => IImportProviderData;

  @Inject({ from: "importMethods" })
  public readonly $importMethods!: IImportProviderMethods;

  // Data

  public isProcessing: string[] = [];

  // Computed

  public get $importData() {
    return this.$importProvider();
  }

  public get messages(): IImportMessage[] {
    const statusCode = this.$importData.import.status.code;
    return [
      // Pending
      {
        if: [ISC.PENDING].includes(statusCode),
        path: "_sentence.imports.import_pending_msg",
        tag: this.$t("_.pending"),
        type: "is-caution",
        slots: () => [
          {
            component: "strong",
            name: "action",
            value: this.$t("_action.start_import")
          },
          {
            component: "span",
            name: "source",
            value: _.get(this.$importData, "import.credentials.source.name")
          }
        ]
      },
      // In Progress
      {
        if: [ISC.IN_PROGRESS].includes(statusCode),
        path: "_sentence.imports.import_in_progress_msg",
        tag: this.$t("_.in_progress"),
        type: "is-info"
      },
      // Mappings Required
      {
        if: [ISC.MAPPINGS_REQUIRED].includes(statusCode),
        path: "_sentence.imports.import_mappings_required_msg",
        icon: "directions",
        type: "is-warning",
        slots: () => {
          const stepIndex = _.findIndex(
            this.$importData.steps,
            step => step.status.code === ISC.MAPPINGS_REQUIRED
          );
          return [
            {
              component: "u-link",
              name: "step",
              events: {
                click: async () => {
                  await this.$router
                    .push({ name: "adminImport" })
                    .catch(err => err);
                  this.$importMethods.openStep(stepIndex);
                }
              },
              value: this.$importData.steps[stepIndex].name
            }
          ];
        }
      },
      // Staged
      {
        if: [ISC.STAGED].includes(statusCode),
        path: "_sentence.imports.import_staged_msg",
        tag: this.$t("_.staged"),
        type: "is-dark",
        action: {
          label: this.$t("_action.commit_import"),
          events: {
            click: () => this.$importMethods.commit()
          }
        }
      },
      // Cancelled
      {
        if: [ISC.CANCELLED].includes(statusCode),
        path: "_sentence.imports.import_cancelled_msg",
        tag: this.$t("_.cancelled"),
        type: "is-grey",
        slots: () => [
          {
            component: "datetime",
            name: "date",
            props: {
              datetime: this.$importData.import.updated_at
            }
          }
        ]
      },
      // Error
      {
        if: [ISC.ERROR].includes(statusCode),
        path: "_sentence.imports.import_error_msg",
        tag: this.$t("_.error"),
        type: "is-danger",
        slots: () => [
          {
            component: "u-link",
            name: "logs",
            props: {
              tag: "router-link",
              to: { name: "adminImportLogs" }
            },
            value: (this.$t("_.import_logs") as string).toLowerCase()
          }
        ]
      },
      // Complete
      {
        if: [ISC.COMPLETE].includes(statusCode),
        path: "_sentence.imports.import_complete_msg",
        tag: this.$t("_.complete"),
        type: "is-success",
        slots: () => [
          {
            component: "datetime",
            name: "date",
            props: {
              datetime: this.$importData.import.completed_at
            }
          }
        ]
      }
    ];
  }

  public get availableMessages() {
    return this.$_.filter(this.messages, msg => msg.if);
  }
}
